import { ActionIcon, Anchor, Button, CopyButton, Group, Loader, Paper, rem, Stack, Text, Tooltip } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import React from 'react';
import { z } from 'zod';

import { useGenerateInvitationUrl, useSendInvitation } from '@hooks/forwarder';
import { TextInput } from './TextInput';

interface InviteOnlineProps {
  onHide(): void;
}

const inviteOnlineSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }).min(1, { message: 'Email required' }),
});

type InviteOnlineFormValues = z.infer<typeof inviteOnlineSchema>;

export const InviteOnline: React.FC<InviteOnlineProps> = ({ onHide }) => {
  const sendInvitation = useSendInvitation();
  const generateInvitationUrlMutation = useGenerateInvitationUrl();
  const [invitationUrl, setInvitationUrl] = React.useState<string | null>(null);

  const form = useForm<InviteOnlineFormValues>({
    initialValues: {
      email: '',
    },
    validate: zodResolver(inviteOnlineSchema),
  });

  const handleSellerSubmit = async (values: typeof form.values) => {
    try {
      await sendInvitation.mutateAsync({ body: { email: values.email } });
      onHide();
    } catch (error) {
      form.setErrors({ email: 'Failed to send invite. Please try again.' });
    }
  };

  const handleGenerateUrl = async () => {
    try {
      const res = await generateInvitationUrlMutation.mutateAsync({ body: { email: form.values.email } });
      setInvitationUrl(res.body.invitationUrl);
    } catch (error) {
      form.setErrors({ email: 'Failed to generate invitation URL. Please try again.' });
    }
  };

  return (
    <Paper>
      <Stack justify="center" align="center" gap="xs" mb="lg">
        <Text c="gray">with an email address</Text>
      </Stack>
      <form onSubmit={form.onSubmit(handleSellerSubmit)}>
        <Stack ml="xl" mr="xl" gap="sm" align="stretch">
          <TextInput label="Enter email address" {...form.getInputProps('email')} />
        </Stack>

        <Group justify="center" mt="xl">
          <Button type="submit" leftSection={sendInvitation.isPending ? <Loader /> : null}>
            Send
          </Button>
          <Button onClick={handleGenerateUrl} loading={generateInvitationUrlMutation.isPending} disabled={!form.values.email || !form.isValid()}>
            Generate Invitation Link
          </Button>
        </Group>
      </form>

      {invitationUrl && (
        <Stack align="center" mb="lg" mt="lg">
          <Text c="gray">Copy this link to forward it</Text>
          <Group gap="xs">
            <Anchor href={invitationUrl} target="_blank">
              {invitationUrl.substring(0, 40)}...
            </Anchor>

            <CopyButton value={invitationUrl} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="right">
                  <ActionIcon color={copied ? 'teal' : 'blue'} variant="subtle" onClick={copy}>
                    {copied ? <IconCheck style={{ width: rem(16) }} /> : <IconCopy style={{ width: rem(16) }} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        </Stack>
      )}
    </Paper>
  );
};
